import { supabase } from './client';

export interface SubscriptionData {
  customer_id: string;
  subscription_id: string | null;
  subscription_status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

export interface OrderData {
  customer_id: string;
  order_id: number;
  checkout_session_id: string;
  payment_intent_id: string;
  amount_subtotal: number;
  amount_total: number;
  currency: string;
  payment_status: string;
  order_status: string;
  order_date: string;
}

export async function createCheckoutSession(
  priceId: string,
  mode: 'payment' | 'subscription' = 'payment'
) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        price_id: priceId,
        mode,
        success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: window.location.href,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('Checkout error response:', responseData);
      throw new Error(responseData.error || `HTTP ${response.status}: Failed to create checkout session`);
    }

    const { url, sessionId } = responseData;
    
    if (url) {
      window.location.href = url;
    } else {
      throw new Error(`No checkout URL received. Session ID: ${sessionId || 'unknown'}`);
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}

export async function getUserSubscription(): Promise<SubscriptionData | null> {
  try {
    const { data, error } = await supabase
      .from('stripe_user_subscriptions')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }
}

export async function getUserOrders(): Promise<OrderData[]> {
  try {
    // Check if we have a valid session first
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return [];
    }

    // Add retry logic for network issues
    let retries = 3;
    let lastError;
    
    while (retries > 0) {
      try {
        const { data, error } = await supabase
          .from('stripe_user_orders')
          .select('*')
          .order('order_date', { ascending: false });

        if (error) {
          console.error('Error fetching orders:', error);
          return [];
        }

        return data || [];
      } catch (fetchError) {
        lastError = fetchError;
        retries--;
        
        if (retries > 0) {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, (4 - retries) * 1000));
        }
      }
    }

    // If all retries failed, throw the last error
    throw lastError;
  } catch (error) {
    // Handle network connectivity issues gracefully
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      console.warn('Network connectivity issue - unable to fetch orders. Operating in offline mode.');
      throw error; // Re-throw to trigger the connection error UI
    } else {
      console.error('Error fetching orders:', error);
    }
    return [];
  }
}